<template>
    <div :class="{['api-key-popup'] :true, ['hide'] : !display}">
        <div class="popup" ref="apiKeyPopupRef">
            <div class="title-section">
                <h2>{{ isEdit ? $t('dash.settings.api_keys.edit.title') : $t('dash.settings.api_keys.create.title') }}</h2>
                <p class="oauth-notice" v-if="oauthMode">{{ $t('dash.settings.api_keys.create.oauth-notice') }}</p>
            </div>

            <div class="container-input name">
                <label>{{ $t('dash.settings.api_keys.create.name.label') }}</label>
                <div class="input">
                    <i class="fa-regular fa-key"></i>
                    <input :placeholder="$t('dash.settings.api_keys.create.name.placeholder')" v-model="form.name">
                    <div class="containerError" v-if="error.name">
                        <Tooltip :position="'top'" :text="error.name">
                            <i class="fa-regular fa-circle-exclamation"></i>
                        </Tooltip>
                    </div>
                </div>
            </div>

            <div class="container-input scopes">
                <label>
                    {{ $t('dash.settings.api_keys.create.scopes.label') }}
                    <span class="toggle-all">
                        <span class="toggle-all-text" @click="toggleAllScopes">{{ allScopesSelected ? $t('dash.settings.api_keys.create.scopes.uncheck-all') : $t('dash.settings.api_keys.create.scopes.check-all') }}</span>
                        <ComponentSwitch :value="allScopesSelected" :onChange="toggleAllScopes" id="all-scopes"></ComponentSwitch>
                    </span>
                </label>
                <div class="box">
                    <div class="scope" v-for="scope in scopes" :key="scope.key">
                        <span>{{ $t(scope.label) }}</span>
                        <ComponentSwitch :value="form.scopes.includes(scope.key)" :onChange="toggleScope" :id="scope.key"></ComponentSwitch>
                    </div>
                </div>
                <div class="containerError" v-if="error.scopes">{{ error.scopes }}</div>
            </div>

            <div class="container-input expires" v-if="!isEdit">
                <label>{{ $t('dash.settings.api_keys.create.expires.label') }}</label>
                <div class="input">
                    <SelectComponent :changeValueSelect="changeExpiresInDays" :data="expirationOptions" :valueSelect="`${form.expires_in_days}`" :positionY="'bottom'" :positionAuto="false"></SelectComponent>
                </div>
            </div>

            <div class="container-input pages">
                <label>{{ $t('dash.settings.api_keys.create.pages.label') }}</label>
                <p class="subtitle">{{ $t('dash.settings.api_keys.create.pages.subtitle') }}</p>

                <div class="page-picker">
                    <input type="text" v-model="pageSearch" :placeholder="$t('dash.settings.api_keys.create.pages.search-placeholder')">

                    <div class="page-list hide-scrollbar">
                        <label class="page-row" v-for="page in filteredPages" :key="page.id">
                            <Avatar :urlPicture="page.avatar" :typeNetwork="page.network" :checked="form.pages_access.includes(page.id)"></Avatar>
                            <span class="page-name">{{ page.name }}</span>
                            <input type="checkbox" :checked="form.pages_access.includes(page.id)" @change="togglePage(page.id, $event.target.checked)">
                        </label>
                        <p class="empty" v-if="!filteredPages.length">{{ $t('dash.settings.api_keys.create.pages.no-results') }}</p>
                    </div>
                </div>
            </div>

            <div class="btns">
                <div class="btn-outline-primary" @click="cancel">{{ $t('dash.settings.api_keys.create.cancel') }}</div>
                <div class="btn-primary icon-r" @click="save">{{ isEdit ? $t('dash.settings.api_keys.edit.save') : $t('dash.settings.api_keys.create.save') }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import ComponentSwitch from './../utils/switch.vue'
import Avatar from './../utils/avatar.vue'
import SelectComponent from './../utils/select.vue'
import Tooltip from './../tooltip/tooltipInit.vue'

const SCOPES = [
    { key: 'bots:read', label: 'dash.settings.api_keys.scope.bots_read' },
    { key: 'bots:write', label: 'dash.settings.api_keys.scope.bots_write' },
    { key: 'posts:read', label: 'dash.settings.api_keys.scope.posts_read' },
    { key: 'posts:write', label: 'dash.settings.api_keys.scope.posts_write' },
    { key: 'moderation:read', label: 'dash.settings.api_keys.scope.moderation_read' },
    { key: 'moderation:write', label: 'dash.settings.api_keys.scope.moderation_write' },
    { key: 'reporting:read', label: 'dash.settings.api_keys.scope.reporting_read' },
    { key: 'sources:read', label: 'dash.settings.api_keys.scope.sources_read' },
    { key: 'sources:write', label: 'dash.settings.api_keys.scope.sources_write' },
]

export default {
    name: 'apiKeyPopup',

    components: {
        ComponentSwitch,
        Avatar,
        SelectComponent,
        Tooltip
    },

    props: {
        display: Boolean,
        accountPages: {
            type: Array,
            default: () => []
        },
        // when set, the popup edits this existing key instead of creating a new one
        data: {
            type: Object,
            default: null
        },
        // OAuth "remote connector" flow: creating the key here also authorizes that connection
        oauthMode: {
            type: Boolean,
            default: false
        },
        defaultName: {
            type: String,
            default: ''
        },
    },

    data() {
        return {
            scopes: SCOPES,
            pageSearch: '',

            form: {
                name: '',
                scopes: [],
                pages_access: [],
                expires_in_days: 90,
            },

            error: {
                name: '',
                scopes: '',
            },
        };
    },

    computed: {
        isEdit() {
            return !!(this.data && this.data.id)
        },

        filteredPages() {
            const q = this.pageSearch.trim().toLowerCase()
            if (!q) return this.accountPages
            return (this.accountPages || []).filter(page => page.name && page.name.toLowerCase().includes(q))
        },

        expirationOptions() {
            return {
                30: this.$t('dash.settings.api_keys.create.expires.30'),
                90: this.$t('dash.settings.api_keys.create.expires.90'),
                180: this.$t('dash.settings.api_keys.create.expires.180'),
                365: this.$t('dash.settings.api_keys.create.expires.365'),
            }
        },

        allScopesSelected() {
            return this.scopes.every(scope => this.form.scopes.includes(scope.key))
        }
    },

    mounted() {
        window.addEventListener('click', this.handleClickOutside);

        if (this.data) {
            this.form.name = this.data.name || ''
            this.form.scopes = Array.isArray(this.data.scopes) ? [...this.data.scopes] : []
            this.form.pages_access = Array.isArray(this.data.pages_access) ? [...this.data.pages_access] : []
        } else if (this.defaultName) {
            this.form.name = this.defaultName
        }
    },

    methods: {
        toggleScope(checked, scopeKey) {
            this.form.scopes = checked
                ? [...this.form.scopes, scopeKey]
                : this.form.scopes.filter(s => s !== scopeKey)
        },

        toggleAllScopes() {
            this.form.scopes = this.allScopesSelected ? [] : this.scopes.map(scope => scope.key)
        },

        togglePage(pageId, checked) {
            this.form.pages_access = checked
                ? [...this.form.pages_access, pageId]
                : this.form.pages_access.filter(id => id !== pageId)
        },

        changeExpiresInDays(value) {
            this.form.expires_in_days = parseInt(value, 10)
        },

        cancel() {
            this.$emit('closePopUp')
        },

        save() {
            this.error = { name: '', scopes: '' }
            let isSuccess = true

            if (!this.form.name.trim()) {
                isSuccess = false
                this.error.name = this.$t('dash.settings.api_keys.create.error.name_required')
            }

            if (this.form.scopes.length === 0) {
                isSuccess = false
                this.error.scopes = this.$t('dash.settings.api_keys.create.error.scope_required')
            }

            if (!isSuccess) return

            const payload = {
                name: this.form.name.trim(),
                scopes: this.form.scopes,
                pages_access: this.form.pages_access,
            }

            if (!this.isEdit) payload.expires_in_days = this.form.expires_in_days
            if (this.isEdit) payload.id = this.data.id

            this.$emit('submit', payload)
        },

        handleClickOutside(event) {
            return // disabled for now, same as add-user-popup
        },
    },

    beforeDestroy() {
        window.removeEventListener('click', this.handleClickOutside);
    },
};
</script>

<style lang="scss" scoped>
.api-key-popup {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    &.hide {
        display: none;
    }

    .title-section {
        margin-bottom: 16px;

        h2 {
            font-size: 18px;
            font-weight: 700;
            color: #121826;
            margin: 0;
        }

        .oauth-notice {
            margin: 6px 0 0;
            font-size: 12px;
            color: #6b1f4d;
            background: #f9f0f5;
            border-radius: 6px;
            padding: 8px 10px;
        }
    }

    .popup {
        background: #fff;
        border-radius: 12px;
        padding: 24px;
        width: 480px;
        max-width: 90vw;
        max-height: 90vh;
        overflow-y: auto;
    }

    .container-input {
        margin-bottom: 16px;

        label {
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-weight: 600;
            font-size: 13px;
            margin-bottom: 6px;

            .toggle-all {
                display: flex;
                align-items: center;
                gap: 6px;
                font-weight: 400;
                // matches .box's 12px right padding below, so this switch lines up with the
                // individual scope switches instead of sitting 12px further right than them.
                padding-right: 12px;
            }
        }

        .subtitle {
            color: #9ba3b7;
            font-size: 12px;
            margin: 0 0 8px;
        }

        .input {
            display: flex;
            align-items: center;
            gap: 8px;
            border: 1px solid #e3e6ee;
            border-radius: 6px;
            padding: 8px 10px;

            input {
                flex-grow: 1;
                border: none;
                outline: none;
                font-size: 13px;
            }
        }

        .box {
            border: 1px solid #e3e6ee;
            border-radius: 8px;
            padding: 4px 12px;
        }

        .scope {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            padding: 8px 0;
            font-size: 13px;
            border-bottom: 1px solid #f1f2f7;

            // Without this, a long label that wraps onto 2 lines shrinks the switch along with
            // it (both are flex items with the default flex-shrink:1) - the switch must keep its
            // fixed 35x20px shape (switch.scss) no matter how much room the label text takes.
            span {
                min-width: 0;
            }

            .switch {
                flex-shrink: 0;
            }

            &:last-child {
                border-bottom: none;
            }
        }

        .containerError {
            color: #e14e4e;
            font-size: 12px;
            margin-top: 4px;
        }
    }

    .page-picker {
        border: 1px solid #e3e6ee;
        border-radius: 8px;
        overflow: hidden;

        > input {
            width: 100%;
            box-sizing: border-box;
            border: none;
            border-bottom: 1px solid #e3e6ee;
            padding: 8px 10px;
            font-size: 13px;
            outline: none;
        }

        .page-list {
            max-height: 220px;
            overflow-y: auto;
        }

        .page-row {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 10px;
            border-bottom: 1px solid #f1f2f7;
            cursor: pointer;

            &:last-child {
                border-bottom: none;
            }

            &:hover {
                background: #f9fafc;
            }

            .page-name {
                flex-grow: 1;
                font-size: 13px;
            }
        }

        .empty {
            padding: 10px;
            font-size: 12px;
            color: #9ba3b7;
        }
    }

    .btns {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 8px;
    }
}
</style>
